import { useCallback, useEffect, useRef, useState } from 'react';

const contentTypeHeader = 'content-type';
const contentDispositionHeader = 'content-disposition';

type FileBlob = {
    blob: Blob;
    filename: string;
};

const getFilenameFromHeaders = (response: Response): string => {
    const contentDisposition = response.headers.get(contentDispositionHeader);

    if (!contentDisposition) {
        throw new Error(`'${contentDispositionHeader}' header not found in response`);
    }

    try {
        return contentDisposition.split('filename=')[1].replace(/"/g, '');
    } catch {
        throw new Error(`Could not parse '${contentDispositionHeader}' header`);
    }
};

const mapToFileBlob = async (response: Response): Promise<FileBlob> => {
    const blob = new Blob([await response.blob()], {
        type: response.headers.get(contentTypeHeader) ?? 'application/octet-stream',
    });

    return {
        blob,
        filename: getFilenameFromHeaders(response),
    };
};

type Downloadable = {
    downloadFile: (request: () => Promise<Response>) => Promise<void>;
};

export const useDownloadFile = (): Downloadable => {
    const anchorRef = useRef<HTMLAnchorElement | null>(null);
    const [blobUrl, setBlobUrl] = useState('');

    useEffect(() => {
        const anchor = document.createElement('a');

        anchor.style.display = 'none';
        anchorRef.current = anchor;

        document.body.appendChild(anchor);

        return () => {
            document.body.removeChild(anchor);
        };
    }, []);

    useEffect(() => {
        const blobUrlToRevoke = blobUrl;

        return () => {
            if (blobUrlToRevoke) {
                URL.revokeObjectURL(blobUrlToRevoke);
            }
        };
    }, [blobUrl]);

    const downloadFile = useCallback(async (request: () => Promise<Response>) => {
        const response = await request();

        if (response.status !== 200) {
            throw new Error(`Failed to download file, status: ${response.status}`);
        }

        const { blob, filename } = await mapToFileBlob(response);
        const url = URL.createObjectURL(blob);

        setBlobUrl(url);

        if (anchorRef.current) {
            anchorRef.current.href = url;
            anchorRef.current.download = filename;
            anchorRef.current.click();
        }
    }, []);

    return { downloadFile };
};
